/**
 * errorLogger.ts — global error capture for admin.votd.io.
 *
 * Catches uncaught JS exceptions, unhandled promise rejections, fetch()
 * failures, and React render errors. Each is POSTed to admin_errors in
 * Supabase Proto so we can see what's broken in production WITHOUT the
 * user needing to copy-paste DevTools output.
 *
 * Wired in once at app boot (before React mounts) via initErrorLogger()
 * in main.tsx. Initialization is idempotent — calling twice is a no-op.
 *
 * Defensive throughout: every public function wraps its body in try/catch
 * so the error logger itself cannot become a source of crashes. If
 * Supabase is unreachable, the logger silently fails. Better to lose the
 * error log than to crash the page trying to record an error.
 *
 * Rate-limited dedup: the same error message is reported at most once
 * per minute. Prevents a tight render loop from spamming 1000 rows.
 *
 * Recursion guard: requests to admin_errors itself are excluded from
 * fetch-failure capture, so a failing INSERT can't trigger another INSERT.
 *
 * Schema: see admin_errors_table_for_self_reporting migration.
 */

import { getClient, resolveAuthEnv } from './supabase';
import { type Env } from './env';

const BUILD_HASH = (import.meta.env.VITE_BUILD_HASH as string | undefined) || 'dev';

type ErrorType = 'js_exception' | 'react_error' | 'fetch_failure' | 'network' | 'rejection';

interface ErrorPayload {
  error_type: ErrorType;
  message: string;
  stack?: string | null;
  route?: string;
  env?: string;
  user_id?: string | null;
  user_agent?: string;
  build_hash?: string;
  url?: string | null;
  status_code?: number | null;
  meta?: Record<string, unknown> | null;
}

let isInitialized = false;
let currentUserId: string | null = null;
const recentMessages = new Map<string, number>(); // dedup: message → lastReportedAt

/** Rate-limit dedup: same message at most once per 60s. */
function shouldRateLimit(message: string): boolean {
  const now = Date.now();
  const last = recentMessages.get(message);
  if (last && now - last < 60_000) return true;
  recentMessages.set(message, now);
  // Prune old entries when map grows too large
  if (recentMessages.size > 200) {
    for (const [k, v] of recentMessages) {
      if (now - v > 60_000) recentMessages.delete(k);
    }
  }
  return false;
}

/**
 * The env an error happened in — and, since 2026-08-22, also the project the
 * error is WRITTEN to. Previously this reported the env from the path while
 * inserting into Proto unconditionally, so once a second env was switched on,
 * every Stage error would have been filed in Proto's admin_errors table with a
 * row saying "stage". A log that names one database and writes to another is
 * worse than no log. Errors now land in the project they came from.
 */
function currentEnv(): Env | null {
  if (typeof window === 'undefined') return null;
  return resolveAuthEnv(window.location.pathname, window.location.search);
}

/** Public — called from auth provider when user signs in/out. */
export function setCurrentUser(userId: string | null) {
  currentUserId = userId;
}

/**
 * Public — manually log an error. Used by ErrorBoundary and by ad-hoc
 * call sites that want to record a non-throwing failure (e.g. a swallowed
 * try/catch site that should still be visible in the error log).
 */
export async function logError(payload: Omit<ErrorPayload, 'env' | 'user_id' | 'user_agent' | 'build_hash'>): Promise<void> {
  try {
    if (typeof window === 'undefined') return;
    if (!payload.message) return;
    if (shouldRateLimit(payload.message)) return;
    const env = currentEnv();
    const client = env ? getClient(env) : null;
    if (!client) return;  // env vars missing — can't log

    const fullPayload: ErrorPayload = {
      ...payload,
      env:        env ?? 'unknown',
      user_id:    currentUserId,
      user_agent: window.navigator.userAgent,
      build_hash: BUILD_HASH,
      route:      payload.route ?? window.location.pathname,
      url:        payload.url ?? window.location.href,
    };

    // Truncate massive stacks so a single error can't bloat the table.
    if (fullPayload.stack && fullPayload.stack.length > 8000) {
      fullPayload.stack = fullPayload.stack.slice(0, 8000) + '\n…[truncated]';
    }

    // Fire-and-forget. Errors here are silent — we cannot afford the
    // logger itself throwing.
    // Through api_admin.log_admin_error (CS-5): accepts aal1 so errors before the authenticator
    // step still land; the door stamps user_id from the session, whatever the payload says.
    //
    // `.then(...)` IS THE SEND. A supabase-js query builder is lazy: the HTTP request is made
    // inside its then(), so `void builder` builds a request and never sends it. That is what this
    // line was (`void client.from('admin_errors').insert(...)`, then `void ...rpc(...)`) until
    // 2026-09-28, and why admin_errors held 0 rows on Stage AND Proto after months of use. The
    // handlers swallow both outcomes on purpose (see the docstring).
    client.schema('api_admin').rpc('log_admin_error', { p_fields: fullPayload })
      .then(() => undefined, () => undefined);
  } catch {
    // Silent: see docstring
  }
}

/** Check whether a URL is the admin_errors endpoint — used to skip
 *  fetch-failure capture for admin_errors itself (prevents recursion).
 */
function isAdminErrorsRequest(url: string): boolean {
  // The log call itself goes to /rest/v1/rpc/log_admin_error (CS-5); the old path was
  // /rest/v1/admin_errors. Both are excluded so a failing log call cannot log itself in a loop.
  return /\/admin_errors($|\?)/.test(url) || /\/rpc\/log_admin_error($|\?)/.test(url);
}

/**
 * Initialize global error capture. Idempotent; safe to call multiple times.
 * Call this ONCE at app boot, BEFORE React mounts, so even errors during
 * React initialization are captured.
 */
export function initErrorLogger() {
  if (isInitialized || typeof window === 'undefined') return;
  isInitialized = true;

  // ── Uncaught JS exceptions ──────────────────────────────────────────
  window.addEventListener('error', (event) => {
    const e = event.error as Error | null;
    void logError({
      error_type: 'js_exception',
      message:    e?.message ?? event.message ?? 'Unknown error',
      stack:      e?.stack ?? null,
      meta: {
        filename: event.filename,
        lineno:   event.lineno,
        colno:    event.colno,
      },
    });
  });

  // ── Unhandled promise rejections ────────────────────────────────────
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    let message = 'Unhandled promise rejection';
    let stack: string | null = null;
    if (reason instanceof Error) {
      message = reason.message;
      stack   = reason.stack ?? null;
    } else if (typeof reason === 'string') {
      message = reason;
    } else {
      try { message = JSON.stringify(reason); }
      catch { message = String(reason); }
    }
    void logError({ error_type: 'rejection', message, stack });
  });

  // ── fetch() wrapper for non-2xx + network failures ──────────────────
  //
  // Wraps window.fetch so every HTTP response is observed. Non-2xx
  // responses (RLS 401s, 403s, 404s, 500s) get logged as 'fetch_failure'.
  // Throws from fetch itself (network down, DNS, CORS, offline) get logged
  // as 'network'. We don't change behavior — the original response or
  // exception still propagates to the caller — we just observe.
  //
  // Recursion guard: admin_errors POSTs are excluded so a failing INSERT
  // can't trigger more INSERTs. Same guard for the count-check ping that
  // the diagnostics banner might issue.
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
    const urlArg = args[0];
    const url = typeof urlArg === 'string'
      ? urlArg
      : urlArg instanceof URL
        ? urlArg.toString()
        : (urlArg as Request).url;

    let response: Response;
    try {
      response = await originalFetch(...args);
    } catch (e) {
      if (!isAdminErrorsRequest(url)) {
        const message = e instanceof Error ? e.message : String(e);
        void logError({
          error_type: 'network',
          message,
          stack: e instanceof Error ? (e.stack ?? null) : null,
          url,
        });
      }
      throw e;
    }

    if (!response.ok && response.status !== 304) {
      if (!isAdminErrorsRequest(url)) {
        // Capture a body snippet for context. Clone the response so the
        // consumer can still read the original body. Skip if the body
        // isn't readable (already consumed, etc.).
        let bodySnippet = '';
        try {
          const cloned = response.clone();
          bodySnippet = (await cloned.text()).slice(0, 600);
        } catch {
          // Body not cloneable; skip
        }
        void logError({
          error_type:  'fetch_failure',
          message:     `HTTP ${response.status} ${response.statusText} on ${url}`,
          status_code: response.status,
          url,
          meta: {
            method:       (args[1] as RequestInit | undefined)?.method ?? 'GET',
            body_snippet: bodySnippet || null,
          },
        });
      }
    }
    return response;
  };
}
